// The mining process. Opt-in only: nothing here runs until start() is called
// from miner.exe. It keeps running when the window is minimized or closed with
// "Keep running", and stops on Stop, Shut Down or tab close.
//
// Hash rate is measured from hashes this tab actually computed. Credits are
// whatever the coordinator says after verifying each share; this file never
// estimates or invents a balance.

import { create } from 'zustand';
import { bitsForRate, nonceHex, zerosFrom, midstate, seedWords, type Challenge, type SubmitResult, type MiningQuota } from '@kumio/shared';
import { api, ApiError } from '../lib/api';
import { useProcesses } from '../os/processes';
import { useSettings } from '../state/settings';
import { useWallet } from '../state/wallet';
import { createGpuMiner, type GpuMiner } from './gpuKernel';
import { detect, gpuAdapter, gpuLabel } from './hw';

export type MinerStatus = 'idle' | 'starting' | 'mining' | 'stopping' | 'error';

export interface MinerLog {
  t: number;
  kind: 'sys' | 'ok' | 'err' | 'info';
  text: string;
}

export interface MinerState {
  status: MinerStatus;
  backend: 'webgpu' | 'cpu' | null;
  device: string;
  threads: number;
  intensity: number; // 0.1 .. 1
  hashrate: number;
  history: number[]; // one sample per second, last 120 s
  bits: number;
  found: number;
  accepted: number;
  rejected: number;
  pending: number;
  sessionCreditedMicro: bigint;
  startedAt: number | null;
  challenge: Challenge | null;
  recipient: string | null;
  lastError: string | null;
  submitOffline: boolean;
  log: MinerLog[];
  quota: MiningQuota | null;
  fallbackReason: string | null;
}

export const useMiner = create<MinerState>(() => ({
  status: 'idle',
  backend: null,
  device: '',
  threads: 0,
  intensity: Math.min(1, Math.max(0.1, Number(localStorage.getItem('kumio.miner.intensity')) || 0.6)),
  hashrate: 0,
  history: [],
  bits: 0,
  found: 0,
  accepted: 0,
  rejected: 0,
  pending: 0,
  sessionCreditedMicro: 0n,
  startedAt: null,
  challenge: null,
  recipient: null,
  lastError: null,
  submitOffline: false,
  log: [],
  quota: null,
  fallbackReason: null,
}));

const set = (p: Partial<MinerState>) => useMiner.setState(p);
const get = () => useMiner.getState();
const MAX_LOG = 250;
const MAX_PENDING = 4000;
function log(kind: MinerLog['kind'], text: string) {
  useMiner.setState((s) => ({ log: [...s.log, { t: Date.now(), kind, text }].slice(-MAX_LOG) }));
}

interface Share {
  challengeId: string;
  bits: number;
  nonce: string;
}

let pending: Share[] = [];
let windowHashes = 0;
let windowStart = 0;
let tick: ReturnType<typeof setInterval> | null = null;
let flushTimer: ReturnType<typeof setInterval> | null = null;
let gpu: GpuMiner | null = null;
let gpuGroups = 128;
let cpuWorkers: Worker[] = [];
let cpuTag = 0;
const tagInfo = new Map<number, { challengeId: string; seed: string }>();
let generation = 0; // bumps on every start/stop so stale loops exit
let flushing = false;
let activeFlush: Promise<void> | null = null;
let leaseToken: string | null = null;
let heartbeatAt = 0;
let heartbeatBusy = false;
let rotating = false;
const leaseHeaders = (): Record<string,string> => leaseToken ? { 'x-kumio-mining': leaseToken } : {};
export async function refreshMiningQuota() {
 const owner = useWallet.getState().session?.address; if (!owner) { set({quota:null});return; }
 try { const quota = await api<MiningQuota>('/v1/mine/quota'); if (useWallet.getState().session?.address === owner) {
  set({quota});
 } } catch {}
}
async function releaseLease(keepalive = false) {
 const token = leaseToken; leaseToken = null; if (!token) return;
 try {await api('/v1/mine/stop',{method:'POST',json:{},headers:{'x-kumio-mining':token},timeoutMs:3000,keepalive});}catch{}
}

const PROC_ID = 'miner';
useProcesses.getState().upsert({ id: PROC_ID, app: 'miner', name: 'miner.exe', active: false, detail: '', startedAt: null, stop: () => stopMining() });

function fmtRate(h: number) {
  const u = ['H/s', 'kH/s', 'MH/s', 'GH/s'];
  let i = 0;
  while (h >= 1000 && i < u.length - 1) {
    h /= 1000;
    i++;
  }
  return h.toFixed(h < 10 ? 2 : h < 100 ? 1 : 0) + ' ' + u[i];
}

async function fetchChallenge(recipient: string, gen = generation): Promise<Challenge> {
  const c = await api<Challenge>(`/v1/mine/challenge?recipient=${recipient}`, {headers:leaseHeaders()});
  if (c.recipient.toLowerCase() !== recipient.toLowerCase()) throw new Error('The coordinator issued a challenge for a different address.');
  if (generation === gen) set({ challenge: c });
  return c;
}

function record(challengeId: string, bits: number, hi: number, lo: number) {
  if (pending.length >= MAX_PENDING) return; // bounded while offline
  pending.push({ challengeId, bits, nonce: nonceHex(hi, lo) });
  set({ found: get().found + 1, pending: pending.length });
}

function flush(): Promise<void> {
 if(activeFlush)return activeFlush;
 if(!pending.length)return Promise.resolve();
 activeFlush=flushBatch().finally(()=>{activeFlush=null;});return activeFlush;
}
async function flushBatch() {
  if (flushing || !pending.length) return;
  const recipient = get().recipient;
  if (!recipient) return;
  flushing = true;
  const gen=generation, headers=leaseHeaders();
  const current=()=>generation===gen || (get().status==='stopping' && generation===gen+1 && leaseToken===headers['x-kumio-mining'] && useWallet.getState().session?.address.toLowerCase()===recipient.toLowerCase());
  const batch = pending;
  pending = [];
  const groups = new Map<string, { challengeId: string; bits: number; nonces: string[] }>();
  for (const s of batch) {
    const k = s.challengeId + ':' + s.bits;
    if (!groups.has(k)) groups.set(k, { challengeId: s.challengeId, bits: s.bits, nonces: [] });
    groups.get(k)!.nonces.push(s.nonce);
  }
  const retry: Share[] = [];
  try {
    for (const g of groups.values()) {
      for (let i = 0; i < g.nonces.length; i += 256) {
        const nonces = g.nonces.slice(i, i + 256);
        try {
          const r = await api<SubmitResult>('/v1/mine/submit', {
            method: 'POST', headers,
            json: { challengeId: g.challengeId, recipient, bits: g.bits, nonces, backend: get().backend, device: get().device.slice(0, 80) },
          });
          if (!current()) return;
          const credited = BigInt(r.creditedMicro);
          set({
            accepted: get().accepted + r.accepted,
            rejected: get().rejected + r.rejected + r.duplicate,
            sessionCreditedMicro: get().sessionCreditedMicro + credited,
            submitOffline: false,
            quota: r.quota || get().quota,
          });
          // Refresh the full authoritative balance, including holds and totals.
          if(r.balance)useWallet.getState().setBalance(r.balance);else void useWallet.getState().refreshBalance();
          if (r.accepted) log('ok', `${r.accepted} share${r.accepted === 1 ? '' : 's'} accepted at ${g.bits} bits · +${(Number(credited) / 1e6).toFixed(6)} cr`);
          if (r.rejected + r.duplicate) log('err', `${r.rejected + r.duplicate} rejected: ${[...new Set(r.reasons)].join(', ') || 'invalid'}`);
        } catch (e) {
          if (!current()) return;
          if (e instanceof ApiError && e.code === 'offline') {
            retry.push(...nonces.map((nonce) => ({ challengeId: g.challengeId, bits: g.bits, nonce })));
            if (!get().submitOffline) log('err', 'coordinator unreachable; shares are kept and retried');
            set({ submitOffline: true });
          } else if (e instanceof ApiError && (e.status === 401 || e.code === 'wrong_session')) {
            log('err', e.message);
            set({ rejected: get().rejected + nonces.length, lastError:e.message });
            // Stop compute when its signed session is no longer valid.
            pending=[]; void stopMining(); void refreshMiningQuota(); return;
          } else if (e instanceof ApiError && e.status === 429) {
            retry.push(...nonces.map((nonce) => ({ challengeId: g.challengeId, bits: g.bits, nonce })));
            log('err', 'rate limited by the coordinator; retrying shortly');
          } else {
            set({ rejected: get().rejected + nonces.length });
            log('err', `submit rejected: ${(e as Error).message}`);
          }
        }
      }
    }
  } finally {
    if (current()) {pending = [...retry, ...pending].slice(0, MAX_PENDING);set({ pending: pending.length });}
    flushing = false;
  }
}

/* --------------------------------------------------------------- GPU loop */

const yieldChannel = typeof MessageChannel === 'function' ? new MessageChannel() : null;
function yieldNow(): Promise<void> {
  if (!yieldChannel) return new Promise((r) => setTimeout(r, 0));
  return new Promise((r) => {
    yieldChannel.port1.onmessage = () => r();
    yieldChannel.port2.postMessage(0);
  });
}

async function gpuLoop(gen: number) {
  let hi = (Math.random() * 2 ** 32) >>> 0;
  let base = 0;
  while (generation === gen && gpu && get().status === 'mining') {
    const ch = get().challenge;
    if (!ch) {
      await new Promise((r) => setTimeout(r, 200));
      continue;
    }
    const bits = get().bits;
    const count = gpuGroups * 256 * gpu.iters;
    if (base + count > 2 ** 32) {
      hi = (hi + 1) >>> 0;
      base = 0;
    }
    let res;
    try {
      res = await gpu.run(ch.seed, hi, base, bits, gpuGroups);
    } catch {
      switchToCpu(gen,'The GPU stopped responding. Mining is continuing on CPU threads.');
      break;
    }
    if (generation !== gen) break;
    const ms = midstate(seedWords(ch.seed));
    for (const lo of res.los) {
      // Re-verify on the CPU: a kernel bug must be loud here, not silent at the coordinator.
      if (zerosFrom(ms, hi, lo) >= bits) record(ch.id, bits, hi, lo);
      else log('err', `GPU reported a share that does not verify (${nonceHex(hi, lo)}); dropped`);
    }
    if (res.count > 48) set({ bits: Math.min(ch.maxBits, bits + 2) });
    base += count;
    windowHashes += count;
    // Size dispatches to ~40–80 ms so the page stays responsive.
    if (res.ms < 40 && gpuGroups < 16384) gpuGroups *= 2;
    else if (res.ms > 90 && gpuGroups > 16) gpuGroups = gpuGroups >> 1;
    const duty = get().intensity;
    const rest = duty >= 1 ? 0 : (res.ms * (1 - duty)) / Math.max(0.05, duty);
    // Short rests use a message-channel yield: background tabs clamp chained
    // timers to as little as one per minute, which would stall the miner.
    if (rest < 8) await yieldNow();
    else await new Promise((r) => setTimeout(r, rest));
  }
}

/* --------------------------------------------------------------- CPU path */

function startCpu() {
  const gen=generation;
  const cores = navigator.hardwareConcurrency || 2;
  const n = Math.max(1, Math.min(8, Math.round((cores - 1) * Math.max(0.25, get().intensity)) || 1));
  for (let i = 0; i < n; i++) {
    const w = new Worker(new URL('../workers/sha.worker.ts', import.meta.url), { type: 'module', name: 'kumio-sha' });
    w.onmessage = (e) => {
      if(generation!==gen || get().status!=='mining')return;
      const m = e.data;
      if (m.type !== 'progress') return;
      windowHashes += m.hashes;
      const info = tagInfo.get(m.tag);
      if (info) for (const [hi, lo, bits] of m.found as [number, number, number][]) record(info.challengeId, bits, hi, lo);
    };
    w.onerror = () => {if(generation!==gen)return;const message=`CPU thread ${i+1} stopped. Restart mining to continue.`;log('err',message);set({lastError:message});void stopMining();};
    cpuWorkers.push(w);
  }
  set({ threads: n });
  postCpuJobs();
}
function switchToCpu(gen: number, reason: string) {
 if(generation!==gen || get().status!=='mining' || get().backend!=='webgpu')return;
 const old=gpu;gpu=null;
 const ch=get().challenge,bench=Number(localStorage.getItem('kumio.bench.cpu'))||2e6;
 set({backend:'cpu',device:`CPU · ${navigator.hardwareConcurrency||2} logical cores`,fallbackReason:reason,hashrate:0,bits:ch?bitsForRate(bench*get().intensity,ch.minBits,ch.maxBits):get().bits});
 old?.destroy();windowHashes=0;windowStart=performance.now();log('err',reason);startCpu();
}

function postCpuJobs() {
  const ch = get().challenge;
  if (!ch || !cpuWorkers.length) return;
  const tag = ++cpuTag;
  tagInfo.set(tag, { challengeId: ch.id, seed: ch.seed });
  for (const k of tagInfo.keys()) if (k < tag - 3) tagInfo.delete(k);
  const baseHi = (Math.random() * 2 ** 32) >>> 0;
  cpuWorkers.forEach((w, i) => w.postMessage({ type: 'job', tag, seed: ch.seed, hi: (baseHi + i * 7919) >>> 0, lo: 0, bits: get().bits, duty: get().intensity }));
}

/* -------------------------------------------------------------- lifecycle */

export async function startMining(): Promise<void> {
  const s = get();
  if (s.status === 'mining' || s.status === 'starting') return;
  if (s.status === 'stopping') throw new Error('Wait for mining to stop before starting again.');
  const wallet = useWallet.getState();
  const recipient = wallet.session?.address;
  if (!recipient || !wallet.connected || recipient.toLowerCase() !== wallet.connected.toLowerCase()) throw new Error('Connect your wallet and sign in before mining.');
  const gen = ++generation;
  set({ status: 'starting', recipient, lastError: null, fallbackReason:null, threads:0, found: 0, accepted: 0, rejected: 0, sessionCreditedMicro: 0n, hashrate: 0, history: [], startedAt: Date.now() });
  useProcesses.getState().patch(PROC_ID, { active: true, detail: 'starting', startedAt: Date.now() });
  log('sys', `starting · credits go to ${recipient.slice(0, 6)}…${recipient.slice(-4)}`);
  try {
    const savedId = (storage: Storage, key: string) => { const old=storage.getItem(key); if (old && /^[a-zA-Z0-9-]{16,64}$/.test(old)) return old; const id=crypto.randomUUID();storage.setItem(key,id);return id; };
    const lease = await api<{token:string;quota:MiningQuota}>('/v1/mine/start',{method:'POST',json:{clientId:savedId(sessionStorage,'kumio.miner.client'),deviceId:savedId(localStorage,'kumio.miner.device')}});
    if (generation !== gen) {try{await api('/v1/mine/stop',{method:'POST',json:{},headers:{'x-kumio-mining':lease.token},timeoutMs:3000});}catch{}return;}
    leaseToken=lease.token; set({quota:lease.quota}); heartbeatAt=Date.now();
    const hw = await detect();
    if (generation !== gen) return;
    set({ device: gpuLabel(hw) });
    const ch = await fetchChallenge(recipient, gen);
    if (generation !== gen) return;
    const forceCpu = useSettings.getState().forceCpuMining;
    let backend: 'webgpu' | 'cpu' = 'cpu';
    if (!forceCpu && hw.webgpu) {
      try {
        const adapter = await gpuAdapter();
        if (!adapter) throw new Error('no adapter');
        const kernel = await createGpuMiner(adapter);
        if (generation !== gen) { kernel.destroy(); return; }
        gpu = kernel;
        gpu.device.lost.then((info) => {
          if (generation === gen && get().status === 'mining' && get().backend === 'webgpu') {
            switchToCpu(gen,`The GPU connection was lost (${info.reason}). Mining is continuing on CPU threads.`);
          }
        });
        backend = 'webgpu';
      } catch (e) {
        log('err', `WebGPU unavailable (${(e as Error).message}); using CPU threads`);
        set({fallbackReason:'GPU mining could not start. CPU threads are active instead.'});
        gpu = null;
      }
    } else if (forceCpu) {set({fallbackReason:'CPU mining is enabled in Settings. Turn it off to use WebGPU when available.'});log('info', 'CPU mining forced in settings');}
    else {set({fallbackReason:'This browser could not access WebGPU. Mining is using CPU threads.'});log('info','WebGPU is not available in this browser. Using CPU threads.');}
    if (generation !== gen) return;
    const bench = Number(localStorage.getItem('kumio.bench.' + backend)) || (backend === 'webgpu' ? 2e8 : 2e6);
    set({ backend, bits: bitsForRate(bench * get().intensity, ch.minBits, ch.maxBits) });
    if(backend==='cpu')set({device:`CPU · ${hw.cores||2} logical cores`});
    else if(hw.fallbackAdapter)set({fallbackReason:'The browser reported a fallback GPU adapter. Its speed can differ from a physical GPU.'});
    log('sys', `challenge ${ch.id.slice(0, 8)} · difficulty ${get().bits} bits (${ch.minBits}–${ch.maxBits} allowed) · expires in ${Math.round((ch.expiresAt - Date.now()) / 60000)} min`);
    set({ status: 'mining' });
    windowHashes = 0;
    windowStart = performance.now();
    if (backend === 'webgpu') {
      log('sys', 'WebGPU kernel compiled · searching');
      void gpuLoop(gen);
    } else {
      startCpu();
      log('sys', `CPU backend · ${get().threads} thread${get().threads > 1 ? 's' : ''}`);
    }
    tick = setInterval(() => void onTick(gen), 1000);
    flushTimer = setInterval(() => void flush(), 1000);
  } catch (e) {
    if (generation !== gen) return;
    const msg = e instanceof ApiError && e.code === 'offline' ? 'The coordinator is unreachable, so shares cannot be verified or credited. Try again later, or run a local benchmark in sysinfo.' : (e as Error).message;
    log('err', msg);
    const cleanupGeneration = generation + 1;
    await teardown();
    if (generation !== cleanupGeneration) return;
    await releaseLease();
    if (generation !== cleanupGeneration) return;
    set({ status: 'error', lastError: msg });
    throw new Error(msg);
  }
}

async function onTick(gen: number) {
  if (generation !== gen) return;
  const now = performance.now();
  const rate = (windowHashes * 1000) / Math.max(1, now - windowStart);
  windowHashes = 0;
  windowStart = now;
  const s = get();
  const hashrate = s.hashrate ? s.hashrate * 0.6 + rate * 0.4 : rate;
  const history = [...s.history, hashrate].slice(-120);
  set({ hashrate, history });
  useProcesses.getState().patch(PROC_ID, { detail: fmtRate(hashrate) });
  const ch = s.challenge;
  if (!ch || s.status !== 'mining') return;
  // Retune difficulty to roughly one share every two seconds.
  const want = bitsForRate(hashrate, ch.minBits, ch.maxBits);
  if (s.startedAt && Date.now() - s.startedAt > 4000 && Math.abs(want - s.bits) >= 2) {
    set({ bits: want });
    if (s.backend === 'cpu') cpuWorkers.forEach((w) => w.postMessage({ type: 'bits', tag: cpuTag, bits: want }));
    log('info', `difficulty retuned to ${want} bits for ${fmtRate(hashrate)}`);
  }
  if (s.backend && s.hashrate > 0) localStorage.setItem('kumio.bench.' + s.backend, String(Math.round(s.hashrate / Math.max(0.1, s.intensity))));
  if (Date.now()-heartbeatAt >= 20000 && !heartbeatBusy) {
    heartbeatBusy=true;
    try {const r=await api<{quota:MiningQuota}>('/v1/mine/heartbeat',{method:'POST',json:{},headers:leaseHeaders(),timeoutMs:8000});if(generation!==gen)return;heartbeatAt=Date.now();set({quota:r.quota});}
    catch(e){if(generation===gen && e instanceof ApiError && e.code!=='offline'){log('err',e.message);set({lastError:e.message});void stopMining();return;}}
    finally{heartbeatBusy=false;}
  }
  if (generation !== gen) return;
  // Rotate before the challenge expires.
  if (ch.expiresAt - Date.now() < 60_000 && !rotating) {
    rotating=true;
    await flush();
    if (generation !== gen) { rotating=false; return; }
    try {
      const next = await fetchChallenge(s.recipient!, gen);
      if (generation !== gen) return;
      log('sys', `new challenge ${next.id.slice(0, 8)}`);
      if (get().backend === 'cpu') postCpuJobs();
    } catch (e) {
      log('err', 'could not rotate challenge: ' + (e as Error).message);
      if (e instanceof ApiError && e.code !== 'offline') void stopMining();
    } finally { rotating=false; }
  }
}

async function teardown() {
  generation++;
  if (tick) clearInterval(tick);
  if (flushTimer) clearInterval(flushTimer);
  tick = flushTimer = null;
  for (const w of cpuWorkers) {
    w.postMessage({ type: 'stop' });
    w.terminate();
  }
  cpuWorkers = [];
  gpu?.destroy();
  gpu = null;
  useProcesses.getState().patch(PROC_ID, { active: false, detail: '', startedAt: null });
}

export async function stopMining(): Promise<void> {
  const s = get();
  if (s.status !== 'mining' && s.status !== 'starting') return;
  set({ status: 'stopping' });
  const stoppedGeneration = generation + 1;
  await teardown();
  if (generation !== stoppedGeneration) return;
  await flush();
  if (generation !== stoppedGeneration) return;
  await flush();
  if (generation !== stoppedGeneration) return;
  await releaseLease();
  if (generation !== stoppedGeneration) return;
  set({ status: 'idle', hashrate: 0, backend: null, threads: 0, device: '', challenge: null, bits: 0, startedAt: null });
  if (pending.length) {log('info', `${pending.length} unsent shares expired with this session. Accepted credits remain saved.`);pending=[];set({pending:0});}
  log('sys', 'stopped');
  void useWallet.getState().refreshBalance();
}

export function setIntensity(v: number) {
  const intensity = Math.max(0.1, Math.min(1, v));
  set({ intensity });
  localStorage.setItem('kumio.miner.intensity', String(intensity));
  for (const w of cpuWorkers) w.postMessage({ type: 'duty', duty: intensity });
}

/** Retry shares kept while offline (called when the coordinator comes back). */
export function retryPending() {
  if (get().status === 'mining' && leaseToken) void flush();
}

/* ------------------------------------------------------------ benchmark */

/** A local measurement only: random seed, nothing submitted, no credits. */
export async function benchmark(ms = 2500): Promise<{ backend: 'webgpu' | 'cpu'; hashrate: number }> {
  if (get().status === 'mining' || get().status === 'starting') throw new Error('Stop the miner first; the benchmark uses the same hardware.');
  const seed = Array.from(crypto.getRandomValues(new Uint8Array(32)), (b) => b.toString(16).padStart(2, '0')).join('');
  const adapter = useSettings.getState().forceCpuMining ? null : await gpuAdapter();
  if (adapter) {
    const g = await createGpuMiner(adapter);
    try {
      let groups = 128;
      let hashes = 0;
      let base = 0;
      const t0 = performance.now();
      while (performance.now() - t0 < ms) {
        const r = await g.run(seed, 1, base, 64, groups);
        hashes += groups * 256 * g.iters;
        base = (base + groups * 256 * g.iters) >>> 0;
        if (r.ms < 40 && groups < 16384) groups *= 2;
      }
      const rate = (hashes * 1000) / (performance.now() - t0);
      localStorage.setItem('kumio.bench.webgpu', String(Math.round(rate)));
      return { backend: 'webgpu', hashrate: rate };
    } finally {
      g.destroy();
    }
  }
  // CPU: measure one worker thread for real, report threads × single-thread rate.
  const threads = Math.max(1, Math.min(8, (navigator.hardwareConcurrency || 2) - 1));
  const w = new Worker(new URL('../workers/sha.worker.ts', import.meta.url), { type: 'module' });
  let hashes = 0;
  const t0 = performance.now();
  await new Promise<void>((res) => {
    w.onmessage = (e) => {
      hashes += e.data.hashes;
      if (performance.now() - t0 > Math.min(ms, 2000)) res();
    };
    w.postMessage({ type: 'job', tag: 0, seed, hi: 0, lo: 0, bits: 64, duty: 1 });
  });
  w.terminate();
  const rate = ((hashes * 1000) / (performance.now() - t0)) * threads;
  localStorage.setItem('kumio.bench.cpu', String(Math.round(rate)));
  return { backend: 'cpu', hashrate: rate };
}

if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', () => {
    // BFCache can restore this same store. Clear the active status as well as
    // workers, including a Start still awaiting network/GPU initialization.
    // Accepted work stays in the server ledger; this page never resumes itself.
    if (get().status === 'idle' || get().status === 'error') return;
    void teardown();
    pending = []; set({ status: 'idle', pending: 0, hashrate: 0, backend: null, threads: 0, device: '', challenge: null, bits: 0, startedAt: null });
    void releaseLease(true);
  });
  window.addEventListener('pageshow', e => {
    if (!e.persisted) return;
    void refreshMiningQuota(); void useWallet.getState().refreshBalance();
  });
}

useWallet.subscribe((next, previous) => {
 const a=next.session?.address, b=previous.session?.address;
 if (a!==b || next.connected?.toLowerCase()!==previous.connected?.toLowerCase()) {
  if(get().status==='mining' || get().status==='starting') {pending=[];set({pending:0});void stopMining();}
  void refreshMiningQuota();
 }
});
